import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import TvCanvas from './TvCanvas';

/* ================= FitText component for auto-shrinking text ================= */
const FitText = ({ children, style = {}, maxFontSize, minFontSize = 8 }) => {
  const ref = useRef(null);
  const [fontSize, setFontSize] = useState(maxFontSize);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const adjustFontSize = () => {
      // Start with the max font size
      const baseSize = maxFontSize || parseInt(style.fontSize) || 22;
      let currentSize = baseSize;

      // Reset to base size first
      element.style.fontSize = `${baseSize}px`;

      // Force a reflow to get accurate measurements
      void element.offsetWidth;

      // Shrink until text fits (with a small buffer for safety)
      while (element.scrollWidth > (element.clientWidth + 1) && currentSize > minFontSize) {
        currentSize -= 0.5;
        element.style.fontSize = `${currentSize}px`;
        void element.offsetWidth; // Force reflow
      }

      setFontSize(currentSize);
    };

    // Initial adjustment with a longer delay to ensure DOM is fully rendered
    const timeout = setTimeout(adjustFontSize, 150);

    // Re-adjust on resize
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(adjustFontSize, 100);
    });
    resizeObserver.observe(element);

    return () => {
      clearTimeout(timeout);
      resizeObserver.disconnect();
    };
  }, [children, style.fontSize, maxFontSize, minFontSize]);

  return (
    <div ref={ref} style={{ ...style, fontSize: `${fontSize}px`, minHeight: 'fit-content' }}>
      {children}
    </div>
  );
};

/* ================= Auto-scroll container ================= */
const AutoScrollContainer = ({ children, style = {} }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    // Check if content overflows
    const checkOverflow = () => {
      const containerHeight = container.clientHeight;
      const contentHeight = content.scrollHeight;
      const hasOverflow = contentHeight > containerHeight + 5; // 5px buffer

      setShouldScroll(hasOverflow);
    };

    // Multiple delayed checks to ensure we catch the overflow after all rendering
    const timeout1 = setTimeout(checkOverflow, 100);
    const timeout2 = setTimeout(checkOverflow, 300);
    const timeout3 = setTimeout(checkOverflow, 600);
    const timeout4 = setTimeout(checkOverflow, 1000);

    // Re-check on resize
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(checkOverflow, 100);
    });
    resizeObserver.observe(container);
    resizeObserver.observe(content);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
      resizeObserver.disconnect();
    };
  }, [children]);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content || !shouldScroll) return;

    let rafId;
    let startTimeout;
    let scrollAccumulator = 0;
    const pixelsPerFrame = 0.3; // Target scroll speed

    const tick = () => {
      const singleContentHeight = content.scrollHeight / 2;

      // Accumulate fractional pixels
      scrollAccumulator += pixelsPerFrame;

      // Only scroll when we have at least 1 full pixel
      if (scrollAccumulator >= 1) {
        const pixelsToScroll = Math.floor(scrollAccumulator);
        container.scrollTop += pixelsToScroll;
        scrollAccumulator -= pixelsToScroll;
      }

      // When we've scrolled past the first copy, seamlessly reset to the beginning
      if (container.scrollTop >= singleContentHeight) {
        container.scrollTop = 0;
      }

      rafId = requestAnimationFrame(tick);
    };

    // Start scrolling after initial 2 second delay
    startTimeout = setTimeout(() => {
      rafId = requestAnimationFrame(tick);
    }, 2000);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(startTimeout);
    };
  }, [shouldScroll]);

  return (
    <div
      ref={containerRef}
      style={{
        ...style,
        overflow: 'hidden', // Changed from 'auto' to 'hidden'
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        height: '100%' // Ensure it has a height
      }}
    >
      <div ref={contentRef}>
        {/* Duplicate content for seamless infinite scroll */}
        {children}
        {shouldScroll && children}
      </div>
    </div>
  );
};

/* ================= Helper to find next upcoming time ================= */
const findNextUpcomingTimeIndex = (items, timezone) => {
  try {
    const now = new Date();

    // Convert current time to minutes since midnight in the shul's timezone
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: timezone || 'America/New_York'
    });

    const currentTimeStr = timeFormatter.format(now);
    const [currentHour, currentMinute, currentSecond] = currentTimeStr.split(':').map(Number);
    const currentMinutes = currentHour * 60 + currentMinute;

    let nextIndex = -1;
    let nextMinutes = Infinity;

    // Find the NEAREST upcoming time (smallest time >= current time)
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Skip non-time items (dividers, custom text, line spaces)
      if (item.isDivider || item.isCustomText || item.isLineSpace) {
        continue;
      }

      // Skip items without a time value (handle both 'time' and 'value' fields)
      const timeValue = item.time || item.value;
      if (!timeValue || typeof timeValue !== 'string') {
        continue;
      }

      // Parse the time string (format: "HH:MM" or "HH:MM:SS" or "H:MM", etc.)
      const timeStr = timeValue.trim();
      const timeParts = timeStr.split(':');

      if (timeParts.length >= 2) {
        let hour = parseInt(timeParts[0], 10);
        const minute = parseInt(timeParts[1], 10);

        if (!isNaN(hour) && !isNaN(minute) && hour >= 0 && hour < 24 && minute >= 0 && minute < 60) {
          // Times are now always in 24-hour format from backend
          let itemMinutes = hour * 60 + minute;

          // If this is a tomorrow time, add 24 hours (1440 minutes)
          if (item.isTomorrow) {
            itemMinutes += 1440;
            // Skip tomorrow times - don't highlight them
            continue;
          }

          // If this time is upcoming AND closer than our current best match
          if (itemMinutes >= currentMinutes && itemMinutes < nextMinutes) {
            nextIndex = i;
            nextMinutes = itemMinutes;
          }
        }
      }
    }

    return nextIndex;
  } catch (error) {
    return -1;
  }
};

/* ================= Helper to format times for display ================= */
const formatTimeForDisplay = (time24h, use12Hour, showAmPm = false, showSeconds = false) => {
  if (!time24h || !use12Hour) {
    // For 24h format, optionally hide seconds
    if (!showSeconds && time24h && time24h.includes(':')) {
      const parts = time24h.split(':');
      if (parts.length === 3) {
        return `${parts[0]}:${parts[1]}`;
      }
    }
    return time24h;
  }

  const parts = time24h.split(':');
  if (parts.length < 2) return time24h;

  let hour = parseInt(parts[0], 10);
  const minute = parts[1];
  const seconds = parts[2] || '';

  if (isNaN(hour)) return time24h;

  // Determine AM/PM
  const ampm = showAmPm ? (hour >= 12 ? ' PM' : ' AM') : '';

  // Convert to 12-hour format
  if (hour === 0) {
    hour = 12;
  } else if (hour > 12) {
    hour = hour - 12;
  }

  // Format the result
  let result = `${hour}:${minute}`;
  if (showSeconds && seconds) {
    result += `:${seconds}`;
  }
  result += ampm;

  return result;
};

/* ================= Clock hook ================= */
const useClock = (twelveHour, timezone) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const options = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: !!twelveHour,
    timeZone: timezone || undefined
  };
  const timeRaw = now.toLocaleTimeString("en-US", options);
  let time = timeRaw.replace(/\s?(AM|PM)/i, ''); // Remove AM/PM

  // Remove leading zero for 12-hour format
  if (twelveHour && time.startsWith('0')) {
    time = time.substring(1);
  }

  const dateOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: timezone || undefined
  };
  const date = now.toLocaleDateString("en-US", dateOptions);
  return { time, date, now };
};

const ShulDisplayV3 = () => {
  const { shulSlug } = useParams();
  const [displayData, setDisplayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detect FireTV or force TV mode with ?tv=1 query parameter
  const isFireTvUA = /AFT|AmazonWebAppPlatform|Fire\s?TV|SmartTV/i.test(navigator.userAgent);
  const forceTv = new URLSearchParams(window.location.search).get('tv') === '1';
  const tvEnabled = forceTv || isFireTvUA;

  // Set --vh CSS variable for better mobile viewport handling
  useEffect(() => {
    const setVH = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  // Get time format settings (with defaults for before data loads)
  const timeFormat = displayData?.shul?.time_format || "24h";
  const showSeconds = displayData?.shul?.show_seconds || false;
  const showAmPm = displayData?.shul?.show_am_pm || false;
  const timezone = displayData?.shul?.timezone || "America/New_York";

  // Use the clock hook (must be called unconditionally) - always shows seconds in header
  const { time: liveClock, date: longDate } = useClock(timeFormat === "12h", timezone);

  // Fetch display data
  const fetchDisplayData = async () => {
    try {
      const data = await api.get(`/display/${shulSlug}/`);
      setDisplayData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching display data:', error);
      setError('Failed to load display data');
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchDisplayData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchDisplayData, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shulSlug]);

  // Get value for an item from the data
  const getItemValue = (item) => {
    if (!displayData) return null;

    const itemId = item.id;

    // Handle different data sources
    if (itemId.startsWith('zmanim_tomorrow_')) {
      const field = itemId.replace('zmanim_tomorrow_', '');
      return displayData.zmanim_tomorrow?.[field];
    } else if (itemId.startsWith('zmanim_')) {
      const field = itemId.replace('zmanim_', '');
      return displayData.zmanim?.[field];
    } else if (itemId.startsWith('limudim_tomorrow_')) {
      const field = itemId.replace('limudim_tomorrow_', '');
      return displayData.limudim_tomorrow?.[field];
    } else if (itemId.startsWith('limudim_')) {
      const field = itemId.replace('limudim_', '');
      return displayData.limudim?.[field];
    } else if (itemId.startsWith('calendar_tomorrow_')) {
      const field = itemId.replace('calendar_tomorrow_', '');
      return displayData.jewish_calendar_tomorrow?.[field];
    } else if (itemId.startsWith('calendar_')) {
      const field = itemId.replace('calendar_', '');
      return displayData.jewish_calendar?.[field];
    } else if (itemId.startsWith('customtext_')) {
      const internalName = itemId.replace('customtext_', '');
      const customText = displayData.custom_texts?.find(ct => ct.internal_name === internalName);
      if (customText) {
        return { _customText: customText };
      }
    } else if (itemId.startsWith('customtime_tomorrow_')) {
      // Tomorrow's custom time
      const internalName = itemId.replace('customtime_tomorrow_', '');
      const customTimeTomorrow = displayData.custom_times_tomorrow?.find(ct => ct.internal_name === internalName);
      if (customTimeTomorrow) {
        return customTimeTomorrow.time;
      }
    } else {
      // Try custom times (today only - don't fallback to tomorrow)
      const customTime = displayData.custom_times?.find(ct => ct.internal_name === itemId);
      if (customTime) {
        return customTime.time;
      }
    }

    return null;
  };

  // Get display name for an item with proper translation
  const getDisplayName = (item) => {
    if (!item || !item.name) return item?.name || '';

    // Use the provided name as the base
    const baseName = item.name;

    // Try to find translated name using the base name
    // The display_names dicts use the full English name as keys
    const zmanimDisplayNames = displayData?.zmanim_display_names || {};
    const limudimDisplayNames = displayData?.limudim_display_names || {};
    const calendarDisplayNames = displayData?.calendar_display_names || {};

    let translatedName = baseName;

    if (zmanimDisplayNames[baseName]) {
      translatedName = zmanimDisplayNames[baseName];
    } else if (limudimDisplayNames[baseName]) {
      translatedName = limudimDisplayNames[baseName];
    } else if (calendarDisplayNames[baseName]) {
      translatedName = calendarDisplayNames[baseName];
    }

    return translatedName;
  };

  // Format value for display
  const formatValue = (value, itemId) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value._customText) return value;

    // Check if this is a time value (zmanim, custom time, or tomorrow variants)
    const isZmanOrCustomTime = itemId && (
      itemId.startsWith('zmanim_') ||
      itemId.startsWith('tomorrow_') ||
      // Custom times are identified by checking if the value is a time string
      // and the item doesn't match other known prefixes
      (!itemId.startsWith('limudim_') &&
       !itemId.startsWith('calendar_') &&
       !itemId.startsWith('customtext_'))
    );

    if (isZmanOrCustomTime && typeof value === 'string' && value.includes(':')) {
      return formatTimeForDisplay(value, timeFormat === "12h", showAmPm, showSeconds);
    }

    return String(value);
  };

  // Helper function to apply opacity to background color only (not text)
  const applyOpacityToColor = (color, opacity) => {
    if (!color || color === 'transparent') return 'transparent';

    const opacityValue = (opacity ?? 100) / 100;

    // If color is in hex format, convert to rgba
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacityValue})`;
    }

    // If already rgba, replace the alpha
    if (color.startsWith('rgba')) {
      return color.replace(/[\d.]+\)$/g, `${opacityValue})`);
    }

    // If rgb, convert to rgba
    if (color.startsWith('rgb')) {
      return color.replace('rgb', 'rgba').replace(')', `, ${opacityValue})`);
    }

    return color;
  };

  // Get background style
  const getBackgroundStyle = () => {
    const layout = displayData?.layout_config || displayData?.layout;
    if (!layout?.globalSettings) {
      return { backgroundColor: '#000' };
    }

    const settings = layout.globalSettings;

    if (settings.backgroundType === 'image' && settings.backgroundImage) {
      return {
        backgroundImage: `url(${settings.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    } else if (settings.backgroundType === 'color') {
      return {
        backgroundColor: applyOpacityToColor(settings.backgroundColor, settings.backgroundOpacity)
      };
    } else {
      return { backgroundColor: '#000' };
    }
  };

  if (loading) {
    return (
      <TvCanvas enabled={tvEnabled} designWidth={1920} designHeight={1080} contain style={{ backgroundColor: '#000' }}>
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 1920,
          height: 1080,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffc764',
          fontSize: '24px'
        }}>
          Loading display...
        </div>
      </TvCanvas>
    );
  }

  if (error) {
    return (
      <TvCanvas enabled={tvEnabled} designWidth={1920} designHeight={1080} contain style={{ backgroundColor: '#000' }}>
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 1920,
          height: 1080,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#e74c3c',
          fontSize: '24px'
        }}>
          {error}
        </div>
      </TvCanvas>
    );
  }

  const layout = displayData?.layout_config || displayData?.layout;

  if (!layout?.version || layout.version !== 'v2') {
    return (
      <TvCanvas enabled={tvEnabled} designWidth={1920} designHeight={1080} contain style={{ backgroundColor: '#000' }}>
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 1920,
          height: 1080,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffc764',
          fontSize: '24px',
          textAlign: 'center',
          padding: '40px'
        }}>
          This display is using the V1 layout.<br/>
          Please use /display/{shulSlug} instead.
        </div>
      </TvCanvas>
    );
  }

  const boxes = layout.boxes || [];
  const globalSettings = layout.globalSettings || {};

  // Check if language is Hebrew (for RTL support)
  const language = displayData?.shul?.language || 'en';
  const isHebrewLanguage = language === 'he' || language === 'sh' || language === 'ah';

  // Helper to get highlight style for next upcoming time
  const getHighlightStyle = (box, isHighlighted) => {
    if (!isHighlighted || !box.styling?.highlightEnabled) return {};

    const highlightType = box.styling.highlightType || 'glow';
    const highlightColor = box.styling.highlightColor || '#00ff00';

    if (highlightType === 'glow') {
      return {
        textShadow: `0 0 8px ${highlightColor}, 0 0 12px ${highlightColor}`,
        filter: 'brightness(1.2)'
      };
    } else if (highlightType === 'color') {
      return {
        color: highlightColor
      };
    }

    return {};
  };

  return (
    <TvCanvas
      enabled={tvEnabled}
      designWidth={1920}
      designHeight={1080}
      contain
      style={{
        overflow: 'hidden',
        fontFamily: 'Arial, sans-serif',
        ...getBackgroundStyle()
      }}
    >
      {/* Header */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        height: 84,
        padding: '20px 40px',
        backgroundColor: applyOpacityToColor(globalSettings.headerBgColor || '#162A45', globalSettings.headerBgOpacity),
        color: globalSettings.headerTextColor || '#ffc764',
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 20,
        boxSizing: 'border-box'
      }}>
        <div style={{ fontSize: '24px', fontWeight: '600', textAlign: 'left' }}>
          {longDate}
        </div>
        <div style={{
          fontSize: '32px',
          fontWeight: '700',
          textAlign: 'center',
          whiteSpace: 'nowrap'
        }}>
          {liveClock}
        </div>
        <div style={{ fontSize: '24px', fontWeight: '600', textAlign: 'right', direction: isHebrewLanguage ? 'rtl' : 'ltr' }}>
          {displayData.formatted_hebrew_date || ''}
        </div>
      </div>

      {/* Main Display Area - Boxes */}
      <div style={{
        position: 'absolute',
        left: 20,
        top: 104,
        width: 1880,
        height: 928
      }}>
        {boxes.map(box => {
          if (!box.showOnDisplay) return null;

          // Handle memorial boxes specially
          if (box.isMemorialBox) {
            const names = box.memorialType === 'ilui'
              ? (displayData.shul?.ilui_nishmat || [])
              : (displayData.shul?.refuah_shleima || []);

            return (
              <div
                key={box.id}
                style={{
                  position: 'absolute',
                  left: `${box.position.x}px`,
                  top: `${box.position.y}px`,
                  width: `${box.position.width}px`,
                  height: `${box.position.height}px`,
                  border: box.styling.showBorder ? `3px solid ${box.styling.borderColor}` : 'none',
                  backgroundColor: applyOpacityToColor(box.styling.backgroundColor, box.styling.backgroundOpacity),
                  borderRadius: '12px',
                  padding: '16px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                {/* Memorial Box Title */}
                <div style={{
                  fontFamily: box.styling.titleFont,
                  color: box.styling.titleColor,
                  fontSize: `${box.styling.titleSize}px`,
                  fontWeight: '600',
                  textAlign: 'center',
                  marginBottom: '12px',
                  direction: 'rtl'
                }}>
                  {box.displayName}
                </div>

                {/* Memorial Names */}
                <AutoScrollContainer style={{
                  flex: 1,
                  fontFamily: box.styling.textFont,
                  color: box.styling.textColor,
                  fontSize: `${box.styling.textSize}px`,
                  textAlign: 'center',
                  direction: 'rtl'
                }}>
                  {names.length > 0 ? (
                    names.map((name, idx) => (
                      <div key={idx} style={{ marginBottom: '8px', whiteSpace: 'nowrap' }}>
                        {name}
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#95a5a6', fontSize: '14px', marginTop: '20px' }}>—</div>
                  )}
                </AutoScrollContainer>
              </div>
            );
          }

          // Prepare box items with their values for highlighting
          const boxItemsWithValues = box.items.map(item => {
            const rawValue = getItemValue(item);
            const displayName = getDisplayName(item);
            const isTomorrow = item.id?.includes('tomorrow');
            return {
              ...item,
              value: rawValue,  // Keep raw value for highlighting calculation
              time: rawValue,   // Also store in 'time' field for compatibility
              displayValue: formatValue(rawValue, item.id),  // Formatted value for display
              displayName,
              isTomorrow
            };
          });

          // Find next upcoming time for this box
          const nextTimeIndex = findNextUpcomingTimeIndex(boxItemsWithValues, timezone);

          return (
            <div
              key={box.id}
              style={{
                position: 'absolute',
                left: `${box.position.x}px`,
                top: `${box.position.y}px`,
                width: `${box.position.width}px`,
                height: `${box.position.height}px`,
                border: box.styling.showBorder ? `3px solid ${box.styling.borderColor}` : 'none',
                backgroundColor: applyOpacityToColor(box.styling.backgroundColor, box.styling.backgroundOpacity),
                borderRadius: '12px',
                padding: '16px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 10,
                // Add background image if enabled
                ...(box.styling.useBackgroundImage && box.styling.backgroundImage && {
                  backgroundImage: `url(${box.styling.backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  position: 'relative'
                })
              }}
            >
              {/* Background Image Overlay for Opacity Control */}
              {box.styling.useBackgroundImage && box.styling.backgroundImage && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '12px',
                  backgroundImage: `url(${box.styling.backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  opacity: (box.styling.backgroundImageOpacity || 100) / 100,
                  pointerEvents: 'none',
                  zIndex: 0
                }} />
              )}

              {/* Content wrapper with relative positioning to stay above background */}
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                {/* Box Title */}
                {box.styling.showTitle !== false && (() => {
                  const isBoxed = box.styling.titleBoxed;
                  const verticalOffset = box.styling.titleVerticalOffset || 0;

                  const titleStyle = {
                    fontFamily: box.styling.titleFont,
                    color: box.styling.titleColor,
                    fontSize: `${box.styling.titleSize}px`,
                    fontWeight: box.styling.titleBold ? 'bold' : 'normal',
                    textAlign: box.styling.titleAlign || 'center',
                    padding: isBoxed ? '8px 16px' : '0',
                    marginBottom: '20px',
                    marginTop: `${verticalOffset}px`,
                    ...(isBoxed && {
                      border: `2px solid ${box.styling.titleBorderColor || box.styling.borderColor || '#d4af37'}`,
                      backgroundColor: box.styling.titleBackgroundColor || box.styling.backgroundColor || 'transparent',
                      borderRadius: '8px',
                    })
                  };

                  return <div style={titleStyle}>{box.displayName}</div>;
                })()}

              {/* Box Content */}
              <AutoScrollContainer style={{
                flex: 1,
                fontFamily: box.styling.textFont,
                color: box.styling.textColor,
                fontSize: `${box.styling.textSize}px`
              }}>
                {boxItemsWithValues.map((item, idx) => {
                  const value = item.value;

                  // Handle custom text items
                  if (value?._customText) {
                    const ct = value._customText;
                    if (ct.text_type === 'divider') {
                      return (
                        <div
                          key={idx}
                          style={{
                            borderBottom: `${ct.line_thickness || 2}px solid ${ct.font_color || box.styling.textColor}`,
                            margin: '8px 0'
                          }}
                        />
                      );
                    } else if (ct.text_type === 'line_space') {
                      return <div key={idx} style={{ height: `${ct.line_thickness || 20}px` }} />;
                    } else {
                      return (
                        <div
                          key={idx}
                          style={{
                            fontSize: `${ct.font_size}px`,
                            color: ct.font_color || box.styling.textColor,
                            textAlign: ct.text_align || 'left',
                            marginBottom: '8px'
                          }}
                        >
                          {ct.text_content}
                        </div>
                      );
                    }
                  }

                  const formatted = item.displayValue;
                  if (!formatted) return null;

                  const displayName = item.displayName;
                  const isTomorrow = item.isTomorrow;
                  const isHighlighted = box.styling.highlightEnabled && idx === nextTimeIndex;
                  const highlightStyle = isHighlighted ? getHighlightStyle(box, true) : {};

                  return (
                    <FitText
                      key={idx}
                      maxFontSize={box.styling.textSize}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr',
                        marginBottom: '8px',
                        padding: '4px 0',
                        ...highlightStyle
                      }}
                    >
                      {isHebrewLanguage ? (
                        <>
                          <div style={{
                            textAlign: 'left',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums'
                          }}>
                            {formatted}
                            {isTomorrow && displayData.tomorrow_translated && (
                              <span style={{ fontSize: '0.8em', marginLeft: '8px', opacity: 0.8 }}>
                                ({displayData.tomorrow_translated})
                              </span>
                            )}
                          </div>
                          <div style={{ margin: '0 12px' }} />
                          <div style={{
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                            direction: 'rtl'
                          }}>
                            {displayName}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{
                            textAlign: 'left',
                            whiteSpace: 'nowrap'
                          }}>
                            {displayName}
                          </div>
                          <div style={{ margin: '0 12px' }} />
                          <div style={{
                            textAlign: 'right',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums'
                          }}>
                            {formatted}
                            {isTomorrow && displayData.tomorrow_translated && (
                              <span style={{ fontSize: '0.8em', marginLeft: '8px', opacity: 0.8 }}>
                                ({displayData.tomorrow_translated})
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </FitText>
                  );
                })}
              </AutoScrollContainer>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 48,
        padding: '10px',
        textAlign: 'center',
        fontSize: '14px',
        color: globalSettings.headerTextColor || '#ffc764',
        backgroundColor: applyOpacityToColor(globalSettings.headerBgColor || '#162A45', globalSettings.headerBgOpacity),
        borderTop: `2px solid ${globalSettings.boxesOutlineColor || '#d4af37'}`
      }}>
        Powered by Shul Schedule - Get your free display at shulschedule.com
      </div>
    </TvCanvas>
  );
};

export default ShulDisplayV3;
